import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Copy, Trash2, TrendingUp, TrendingDown, CheckCircle2, ArrowRight, ChevronDown, Coins, Dog } from 'lucide-react';
import CryptoAffiliateBlock from '../../../components/CryptoAffiliateBlock';
import RiskAnalysisBlock from '../../../components/RiskAnalysisBlock';

const DogecoinProfitCalculatorTool = () => {
  const [buyPrice, setBuyPrice] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [investment, setInvestment] = useState('');
  const [fees, setFees] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');
  const [showCoinMenu, setShowCoinMenu] = useState(false);

  useEffect(() => {
    calculate();
  }, [buyPrice, sellPrice, investment, fees]);

  const calculate = () => {
    const buy = parseFloat(buyPrice);
    const sell = parseFloat(sellPrice);
    const invest = parseFloat(investment);
    const fee = parseFloat(fees) || 0;

    if (isNaN(buy) || isNaN(sell) || isNaN(invest) || buy <= 0 || invest <= 0) {
      setResult(null);
      return;
    }

    const dogePurchased = invest / buy;
    const revenue = dogePurchased * sell;
    const grossProfit = revenue - invest;
    const netProfit = grossProfit - fee;
    const roi = (netProfit / invest) * 100;
    const breakEven = (invest + fee) / dogePurchased;

    setResult({
      dogePurchased,
      revenue,
      grossProfit,
      netProfit,
      roi,
      breakEven,
      isProfitable: netProfit > 0,
      isBreakEven: Math.abs(netProfit) < 0.01
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(typeof value === 'number' ? value.toFixed(2) : value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setBuyPrice('');
    setSellPrice('');
    setInvestment('');
    setFees('');
    setResult(null);
  };

  const formatCurrency = (value) => '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatPercent = (value) => (value >= 0 ? '+' : '') + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
  const formatDoge = (value) => value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const scenarios = [
    { label: 'DOGE: $0.05 → $0.15', buy: '0.05', sell: '0.15', invest: '500' },
    { label: 'DOGE: $0.08 → $0.30', buy: '0.08', sell: '0.30', invest: '1000' },
    { label: 'DOGE: $0.10 → $0.50', buy: '0.10', sell: '0.50', invest: '2000' },
  ];

  const applyScenario = (scenario) => {
    setBuyPrice(scenario.buy);
    setSellPrice(scenario.sell);
    setInvestment(scenario.invest);
    setFees('5');
  };

  const coinOptions = [
    { name: 'Dogecoin (DOGE)', slug: 'dogecoin-profit-calculator', icon: 'Ð', current: true },
    { name: 'Bitcoin (BTC)', slug: 'bitcoin-profit-calculator', icon: '₿', current: false },
    { name: 'Ethereum (ETH)', slug: 'ethereum-profit-calculator', icon: 'Ξ', current: false },
    { name: 'Solana (SOL)', slug: 'solana-profit-calculator', icon: '◎', current: false },
    { name: 'Cardano (ADA)', slug: 'cardano-profit-calculator', icon: '₳', current: false },
    { name: 'All Crypto', slug: 'crypto-profit-calculator', icon: '🪙', current: false },
  ];

  return (
    <div className="space-y-8" data-testid="dogecoin-profit-calculator">
      {/* Coin Selector */}
      <div className="relative">
        <button
          onClick={() => setShowCoinMenu(!showCoinMenu)}
          className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-lg text-yellow-400 hover:bg-yellow-500/30 transition-colors"
          data-testid="coin-selector"
        >
          <span className="text-xl font-bold">Ð</span>
          <span className="font-medium">Dogecoin Profit Calculator</span>
          <ChevronDown size={16} className={`transition-transform ${showCoinMenu ? 'rotate-180' : ''}`} />
        </button>
        
        {showCoinMenu && (
          <div className="absolute top-full left-0 mt-2 w-72 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
            {coinOptions.map((coin) => (
              coin.current ? (
                <div key={coin.slug} className="flex items-center gap-3 px-4 py-3 bg-yellow-500/10 text-yellow-400 cursor-default">
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <CheckCircle2 size={16} className="ml-auto" />
                </div>
              ) : (
                <Link
                  key={coin.slug}
                  to={`/tools/${coin.slug}`}
                  className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700 transition-colors"
                  onClick={() => setShowCoinMenu(false)}
                >
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <ArrowRight size={16} className="ml-auto opacity-50" />
                </Link>
              )
            ))}
          </div>
        )}
      </div>

      {/* Quick Examples */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-400 mr-2">Quick Examples:</span>
        {scenarios.map((scenario, index) => (
          <button
            key={index}
            onClick={() => applyScenario(scenario)}
            className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-full text-gray-300 transition-colors"
          >
            {scenario.label}
          </button>
        ))}
      </div>

      {/* Calculator */}
      <div className="p-6 bg-gray-800 rounded-xl space-y-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
            <span className="text-2xl font-bold text-yellow-400">Ð</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Dogecoin Profit Calculator</h3>
            <p className="text-sm text-gray-400">Calculate your DOGE investment profit — much wow!</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Buy Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={buyPrice}
                onChange={(e) => setBuyPrice(e.target.value)}
                placeholder="e.g., 0.08"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none text-lg"
                data-testid="buy-price-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Sell Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={sellPrice}
                onChange={(e) => setSellPrice(e.target.value)}
                placeholder="e.g., 0.20"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none text-lg"
                data-testid="sell-price-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Investment Amount (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={investment}
                onChange={(e) => setInvestment(e.target.value)}
                placeholder="e.g., 500"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none text-lg"
                data-testid="investment-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Trading Fees (USD) <span className="text-gray-500">(optional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={fees}
                onChange={(e) => setFees(e.target.value)}
                placeholder="e.g., 5"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">DOGE fees are low (~$0.01 per transaction)</p>
          </div>
        </div>

        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors text-sm"
        >
          <Trash2 size={14} />
          Clear All
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          <div className={`p-6 rounded-xl border-2 ${result.isProfitable ? 'bg-green-500/10 border-green-500/40' : result.isBreakEven ? 'bg-yellow-500/10 border-yellow-500/40' : 'bg-red-500/10 border-red-500/40'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {result.isProfitable ? <TrendingUp className="text-green-400" size={28} /> : <TrendingDown className="text-red-400" size={28} />}
                <span className="text-lg font-medium text-gray-200">
                  {result.isProfitable ? 'Much Profit! 🐕' : result.isBreakEven ? 'Break Even' : 'Such Loss 😢'}
                </span>
              </div>
              <button onClick={() => copyResult(result.netProfit, 'profit')} className="p-2 hover:bg-gray-700 rounded-lg">
                {copied === 'profit' ? <CheckCircle2 size={18} className="text-green-400" /> : <Copy size={18} className="text-gray-400" />}
              </button>
            </div>
            <div className="text-center">
              <p className={`text-5xl font-bold ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {result.netProfit >= 0 ? '+' : ''}{formatCurrency(result.netProfit)}
              </p>
              <p className={`text-2xl mt-2 ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {formatPercent(result.roi)} ROI
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">DOGE Purchased</p>
              <p className="text-xl font-semibold text-yellow-400">{formatDoge(result.dogePurchased)} DOGE</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Sale Value</p>
              <p className="text-xl font-semibold text-white">{formatCurrency(result.revenue)}</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Gross Profit</p>
              <p className={`text-xl font-semibold ${result.grossProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {result.grossProfit >= 0 ? '+' : ''}{formatCurrency(result.grossProfit)}
              </p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Break-even Price</p>
              <p className="text-xl font-semibold text-orange-400">{formatCurrency(result.breakEven)}</p>
            </div>
          </div>

          {/* Risk Analysis Block */}
          <RiskAnalysisBlock 
            investmentAmount={parseFloat(investment) || 0}
            currentValue={result.revenue}
            profit={result.netProfit}
          />

          <CryptoAffiliateBlock 
            profit={result.netProfit} 
            cryptoName="DOGE"
            toolName="Dogecoin Profit Calculator"
            toolSlug="dogecoin-profit-calculator"
            investmentAmount={parseFloat(investment) || 0}
          />
        </div>
      )}

      {/* Related Calculators */}
      <div className="p-5 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-xl border border-yellow-500/20">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Coins size={20} className="text-yellow-400" />
          Related Crypto Calculators
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link to="/tools/crypto-profit-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-green-400">Crypto Profit</p>
              <p className="text-sm text-gray-400">All coins</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-green-400" />
          </Link>
          <Link to="/tools/crypto-roi-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-blue-400">Crypto ROI</p>
              <p className="text-sm text-gray-400">Return analysis</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-blue-400" />
          </Link>
          <Link to="/tools/crypto-tax-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-purple-400">Crypto Tax</p>
              <p className="text-sm text-gray-400">Tax estimates</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-purple-400" />
          </Link>
          <Link to="/tools/bitcoin-profit-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-orange-400">Bitcoin Profit</p>
              <p className="text-sm text-gray-400">BTC calculator</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-orange-400" />
          </Link>
        </div>
      </div>

      {/* SEO Content */}
      <div className="space-y-8 pt-6 border-t border-gray-700">
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">What is a Dogecoin Profit Calculator?</h2>
          <div className="prose prose-invert max-w-none text-gray-300 space-y-4">
            <p>
              A <strong>Dogecoin Profit Calculator</strong> is a specialized tool for DOGE investors that calculates 
              your profit or loss from trading the internet's favorite meme coin. Created in 2013 as a joke, Dogecoin 
              has evolved into a legitimate cryptocurrency with a passionate community and real-world utility. With 
              celebrity endorsements from Elon Musk and mainstream adoption by companies like Tesla (briefly) and 
              AMC Theatres, DOGE has proven that meme coins can generate serious returns.
            </p>
            <p>
              Our <strong>DOGE profit calculator</strong> helps you determine exactly how much profit you've made 
              (or could make) by entering your buy price, sell price, and investment amount. Dogecoin's extremely 
              low transaction fees (typically under $0.01) make it efficient for both trading and tipping, which 
              is why we factor in minimal fees by default. The calculator instantly shows your total DOGE acquired, 
              sale value, net profit, and return on investment (ROI).
            </p>
            <p>
              Whether you bought during the 2021 hype cycle, accumulated during the bear market, or are planning 
              your next trade based on social media trends, this calculator gives you precise numbers. Dogecoin's 
              inflationary supply (5 billion new DOGE per year) means price dynamics differ from deflationary coins 
              like Bitcoin, making profit tracking even more important for long-term holders and active traders alike.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">How to Use the Dogecoin Profit Calculator</h2>
          <div className="space-y-3">
            {[
              { step: 1, title: 'Enter Your DOGE Buy Price', desc: 'Input the price per Dogecoin when you purchased. DOGE typically trades under $0.50, so use decimals (e.g., 0.08).' },
              { step: 2, title: 'Enter Your DOGE Sell Price', desc: 'Input the price at which you sold (or plan to sell). Check current DOGE prices on exchanges like Binance or Coinbase.' },
              { step: 3, title: 'Enter Your Investment Amount', desc: 'How much USD you invested. The calculator shows how many DOGE coins you acquired at your buy price.' },
              { step: 4, title: 'Add Trading Fees (Optional)', desc: 'Include exchange trading fees. DOGE network fees are negligible (~$0.01), but exchange fees vary (0.1%-0.5%).' },
              { step: 5, title: 'View Your Results', desc: 'Instantly see your profit/loss in USD, ROI percentage, total DOGE quantity, and break-even price. Much wow!' },
            ].map((item) => (
              <div key={item.step} className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <div className="w-8 h-8 rounded-full bg-yellow-500/20 text-yellow-400 flex items-center justify-center font-bold flex-shrink-0">{item.step}</div>
                <div>
                  <h3 className="font-semibold text-white">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Example Dogecoin Profit Calculation</h2>
          <div className="p-5 bg-gray-800 rounded-lg border border-gray-700">
            <h3 className="font-semibold text-white mb-3">Scenario: DOGE Trade During Elon Tweet Rally</h3>
            <div className="space-y-3 text-gray-300">
              <p>Let's calculate profit from a typical Dogecoin trade based on social media momentum:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Buy Price:</strong> $0.08 per DOGE</li>
                <li><strong>Sell Price:</strong> $0.25 per DOGE</li>
                <li><strong>Investment:</strong> $800</li>
                <li><strong>Trading Fees:</strong> $4 (0.5% total)</li>
              </ul>
              <div className="mt-4 p-4 bg-gray-900 rounded-lg font-mono text-sm">
                <p><span className="text-gray-400">DOGE Purchased:</span> <span className="text-yellow-400">$800 / $0.08 = 10,000 DOGE</span></p>
                <p><span className="text-gray-400">Sale Value:</span> <span className="text-white">10,000 DOGE × $0.25 = $2,500</span></p>
                <p><span className="text-gray-400">Gross Profit:</span> <span className="text-green-400">$2,500 - $800 = $1,700</span></p>
                <p><span className="text-gray-400">Net Profit:</span> <span className="text-green-400">$1,700 - $4 = $1,696</span></p>
                <p><span className="text-gray-400">ROI:</span> <span className="text-blue-400">($1,696 / $800) × 100 = 212%</span></p>
              </div>
              <p className="text-sm text-gray-400 mt-3">
                In this example, an $800 investment turned into $2,496 after fees — a 212% return. This demonstrates 
                why Dogecoin attracts traders looking for high-volatility opportunities, though such gains come with 
                equally high risks.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: 'Is Dogecoin a good investment?', a: 'Dogecoin is highly speculative and volatile. While early investors saw massive gains (DOGE rose over 12,000% in 2021), it\'s also experienced 80%+ crashes. Only invest what you can afford to lose, and never invest based solely on memes or celebrity tweets.' },
              { q: 'What was Dogecoin\'s all-time high?', a: 'Dogecoin reached approximately $0.74 on May 8, 2021, during the SNL Elon Musk episode hype. Many traders calculate potential profits based on DOGE returning to or exceeding this level, though past performance doesn\'t guarantee future results.' },
              { q: 'Why does Elon Musk affect Dogecoin price?', a: 'Elon Musk\'s tweets about DOGE have historically caused significant price movements due to his massive following and influence. He\'s called himself the "Dogefather" and briefly accepted DOGE for Tesla merchandise. However, trading based on tweets is extremely risky.' },
              { q: 'Does Dogecoin have a maximum supply?', a: 'No. Unlike Bitcoin\'s 21 million cap, Dogecoin has no maximum supply. Approximately 5 billion new DOGE are mined each year, creating inflation. This means price growth requires constant demand increase to offset new supply.' },
              { q: 'What are Dogecoin transaction fees?', a: 'Dogecoin has very low network fees — typically around $0.01-0.02 per transaction. This makes it practical for tipping and small payments. Exchange trading fees (0.1%-0.5%) are separate and vary by platform.' },
            ].map((faq, i) => (
              <div key={i} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <h3 className="font-semibold text-white mb-2">{faq.q}</h3>
                <p className="text-gray-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="p-4 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-lg border border-yellow-500/20">
        <h4 className="text-sm font-medium text-gray-200 mb-2 flex items-center gap-2">
          <span className="text-xl">🐕</span>
          The Doge Community
        </h4>
        <p className="text-sm text-gray-400">
          Dogecoin's strength lies in its community — "the people's crypto." From tipping content creators to funding 
          NASCAR sponsorships and Olympic athletes, DOGE holders have shown remarkable community spirit. While 
          calculating profits, remember that many hold DOGE for the fun and community as much as financial gains.
        </p>
      </div>
    </div>
  );
};

export default DogecoinProfitCalculatorTool;
